import { TrendingUp, TrendingDown, Minus } from 'lucide-react';
import type { ComponentType, ReactNode, SVGProps } from 'react';

import { Card, CardBody } from './Card';
import { Skeleton } from './Skeleton';
import type { PanelTone } from './tone';
import { TONE_STYLES } from './tone';

export interface StatTileTrend {
  value: string;
  direction: 'up' | 'down' | 'flat';
}

export interface StatTileProps {
  label: string;
  value: ReactNode;
  hint?: ReactNode;
  icon: ComponentType<SVGProps<SVGSVGElement>>;
  tone?: PanelTone;
  trend?: StatTileTrend;
  loading?: boolean;
}

const TREND_ICON = { up: TrendingUp, down: TrendingDown, flat: Minus };
const TREND_COLOR = {
  up: 'text-emerald-600 dark:text-emerald-400',
  down: 'text-rose-600 dark:text-rose-400',
  flat: 'text-zinc-500 dark:text-zinc-400',
};

/** Rich stat tile — icon-tinted, hover-lift by default, optional trend accent. */
export function StatTile({
  label,
  value,
  hint,
  icon: Icon,
  tone = 'brand',
  trend,
  loading = false,
}: StatTileProps): JSX.Element {
  const t = TONE_STYLES[tone];
  const TrendIcon = trend ? TREND_ICON[trend.direction] : null;

  return (
    <Card interactive className="relative overflow-hidden group h-full">
      <CardBody className="p-5 flex flex-col gap-2 h-full">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm font-medium text-zinc-500 dark:text-zinc-400">{label}</span>
          <div
            className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${t.iconBg}`}
          >
            <Icon className={`w-4 h-4 ${t.text}`} />
          </div>
        </div>
        {loading ? (
          <Skeleton className="h-8 w-16" />
        ) : (
          <span className="text-2xl font-display font-semibold text-zinc-900 dark:text-white tracking-tight">
            {value}
          </span>
        )}
        <div className="flex items-center justify-between gap-2 mt-auto">
          {hint !== undefined && (
            <span className="text-xs text-zinc-400 dark:text-zinc-500">{hint}</span>
          )}
          {trend && TrendIcon && (
            <span
              className={`flex items-center gap-1 text-xs font-medium ${TREND_COLOR[trend.direction]}`}
            >
              <TrendIcon className="w-3 h-3" />
              {trend.value}
            </span>
          )}
        </div>
      </CardBody>
    </Card>
  );
}
